import ErrorPage from 'components/alerts/error-page'
import { FetchError, NotFoundError, UnauthorizedError } from 'errors'
import { MachinesEmptyError, ToolLibrariesEmptyError } from 'errors'
import { WithRouterProps } from 'next/dist/client/with-router'
import { withRouter } from 'next/router'
import { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/browser'

interface Props extends WithRouterProps {
  children?: ReactNode
}

interface State {
  hasError: boolean
  unknownError: boolean
  title?: string
  message?: string
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    unknownError: false,
  }

  public componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.router.asPath !== this.props.router.asPath) {
      this.setState({
        title: undefined,
        message: undefined,
        hasError: false,
        unknownError: false,
      })
    }
  }

  public static getDerivedStateFromError(error: Error) {
    switch (error.message) {
      case new NotFoundError().message:
        return {
          title: 'Error 404',
          message: 'This page could not be found.',
          hasError: true,
        }
      case new UnauthorizedError().message:
        return {
          title: 'Unauthorized',
          message: 'You are unauthorized to view this page.',
          hasError: true,
        }
      case new FetchError().message:
        return {
          title: 'Data Fetching Error',
          message: error.message,
          hasError: true,
        }
      case new MachinesEmptyError().message:
        return {
          title: 'No Machines Found',
          message: error.message,
          hasError: true,
        }
      case new ToolLibrariesEmptyError().message:
        return {
          title: 'No Tool Libraries Found',
          message: error.message,
          hasError: true,
        }
      default:
        return {
          title: undefined,
          message: error.message,
          hasError: true,
          unknownError: true,
        }
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.state.unknownError) {
      Sentry.withScope((scope) => {
        for (const key in errorInfo) {
          scope.setExtra(key, errorInfo[key])
        }
        Sentry.captureException(error)
      })
    }
  }

  public render() {
    const { hasError, message, title } = this.state
    const { children } = this.props

    return !hasError ? children : <ErrorPage {...{ title, message }} />
  }
}

export default withRouter(ErrorBoundary)
